// Taxonomy Generator design tokens + shared primitives.
//
// This is the module's single source of truth — all 11 pages import from here,
// and TaxonomyApp re-exports it rather than keeping its own palette.
//
// Values come from the Orbit design system (shared/styles/tokens.js). The
// helpers below still return inline style objects so every existing call site
// keeps working; page-by-page they are being swapped for the `o-` classes in
// shared/styles/orbit.css, which give real hover/focus/underline states.

import { useState, useRef, useEffect, useCallback } from 'react';
import { T } from '../../../shared/styles/tokens.js';

export const C = {
  // Surfaces
  bg:         T.paper,
  surface:    T.surface,
  s2:         T.surface2,
  s3:         T.surface2,
  border:     T.rule,
  borderSoft: T.ruleSoft,

  // Text
  text:  T.text,
  dim:   T.text2,
  muted: T.text3,

  // Semantic roles. Use these, not `teal`.
  ink:       T.ink,      // primary actions, filled buttons
  brass:     T.brass,    // active underlines, focus rings, mono numerals — rationed
  brassLift: T.brassLift,
  brassWash: T.brassWash,
  code:      T.code,     // taxonomy strings, codes, data emphasis
  codeWash:  T.codeWash,

  danger:  T.error,
  success: T.valid,
  warn:    T.warn,

  // Type
  mono:    T.fontMono,
  display: T.fontDisplay,

};

// ── Inputs ───────────────────────────────────────────────────────────────────

export const inp = (extra = {}) => ({
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: T.rSm,
  padding: '9px 11px',
  color: C.text,
  fontSize: T.tBody,
  fontFamily: 'inherit',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  ...extra,
});

// ── Buttons ──────────────────────────────────────────────────────────────────
// Primary is ink-filled. Brass is never a button fill.

export const btn = (variant = 'default', extra = {}) => {
  const fills = {
    primary: { background: C.ink,     borderColor: C.ink,     color: T.textInvert },
    danger:  { background: C.danger,  borderColor: C.danger,  color: '#fff' },
    success: { background: C.success, borderColor: C.success, color: '#fff' },
    ghost:   { background: 'transparent', borderColor: 'transparent', color: C.dim },
    default: { background: C.surface, borderColor: C.border,  color: C.text },
  };
  const fill = fills[variant] || fills.default;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 13px',
    borderRadius: T.rSm,
    cursor: 'pointer',
    fontSize: T.tSmall,
    fontFamily: 'inherit',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    border: '1px solid',
    ...fill,
    ...extra,
  };
};

export const btnSm = (variant = 'default', extra = {}) =>
  btn(variant, { padding: '4px 10px', fontSize: '.75rem', fontWeight: 500, ...extra });

// ── Type ─────────────────────────────────────────────────────────────────────

export const label = (extra = {}) => ({
  fontSize: T.tLabel,
  fontWeight: 600,
  letterSpacing: '.09em',
  color: C.muted,
  textTransform: 'uppercase',
  ...extra,
});

export const title = (extra = {}) => ({
  fontFamily: C.display,
  fontSize: T.tDisplay,
  fontWeight: 600,
  letterSpacing: '-.022em',
  lineHeight: 1.15,
  margin: 0,
  ...extra,
});

export const sub = (extra = {}) => ({
  color: C.muted,
  fontSize: T.tSmall,
  margin: '3px 0 0',
  ...extra,
});

// Mono code chip. `tone: 'ink'` = white-on-ink; `'code'` = green code wash.
export const codeChip = (tone = 'ink', extra = {}) => ({
  fontFamily: C.mono,
  fontSize: T.tLabel,
  fontWeight: 600,
  letterSpacing: '.03em',
  padding: '3px 6px',
  borderRadius: 6,
  ...(tone === 'code'
    ? { background: C.codeWash, color: C.code }
    : { background: C.ink, color: '#fff' }),
  ...extra,
});

// ── Containers ───────────────────────────────────────────────────────────────

export const panel = (extra = {}) => ({
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: T.rMd,
  boxShadow: T.shadow,
  ...extra,
});

export const card = {
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: T.rMd,
  boxShadow: T.shadow,
  padding: `${T.s4}px ${T.s5}px`,
  marginBottom: T.s4,
};

// Field row — numbered, draggable list rows (taxonomy fields, dictionary values).
export const frow = (extra = {}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: T.s4,
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: T.rMd,
  padding: `11px ${T.s4}px`,
  marginBottom: 6,
  ...extra,
});

// ── Tables ───────────────────────────────────────────────────────────────────

export const th = {
  padding: `11px ${T.s4}px`,
  textAlign: 'left',
  fontSize: T.tLabel,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '.09em',
  color: C.muted,
  borderBottom: `1px solid ${C.border}`,
  background: C.s2,
  whiteSpace: 'nowrap',
};

export const td = {
  padding: `11px ${T.s4}px`,
  borderBottom: `1px solid ${C.borderSoft}`,
  fontSize: T.tSmall,
  color: C.text,
  verticalAlign: 'middle',
};

// Mono numeral cell — counts, row numbers. One of brass's few sanctioned uses.
export const tdNum = {
  ...td,
  fontFamily: C.mono,
  color: C.brass,
  fontWeight: 500,
};

// ── Components ───────────────────────────────────────────────────────────────

// Soft badge: tinted background, coloured text. `color` defaults to brass.
export function Badge({ children, color }) {
  const c = color || C.brass;
  const wash = c === C.brass ? C.brassWash
    : c === C.success ? T.validWash
    : c === C.danger ? T.errorWash
    : `${c}1A`;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 7px',
      borderRadius: 6,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: '.05em',
      textTransform: 'uppercase',
      background: wash,
      color: c,
    }}>
      {children}
    </span>
  );
}

export function Err({ msg }) {
  if (!msg) return null;
  return <div style={{ color: C.danger, fontSize: T.tSmall, marginTop: T.s2 }}>{msg}</div>;
}

export function Spinner() {
  return <div className="o-state">Loading…</div>;
}

export function SectionLabel({ children, style }) {
  return <div style={{ ...label(), marginBottom: 7, ...style }}>{children}</div>;
}

export function SectionHeader({ title: t, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: T.s4 }}>
      <div style={label()}>{t}</div>
      {action && <div>{action}</div>}
    </div>
  );
}

// Sidebar row. Active state is the reference's brass inset rule, not a fill.
export function ListItem({ active, onClick, onDelete, children }) {
  return (
    <div
      onClick={onClick}
      aria-current={active ? 'true' : undefined}
      style={{
        padding: '8px 11px',
        borderRadius: T.rSm,
        marginBottom: 3,
        cursor: 'pointer',
        background: active ? C.surface : 'transparent',
        boxShadow: active ? `inset 2px 0 0 ${C.brass}, ${T.shadow}` : 'none',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <span style={{
        fontSize: T.tSmall,
        fontWeight: active ? 600 : 400,
        color: active ? C.text : C.dim,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {children}
      </span>
      {onDelete && (
        <button
          onClick={e => { e.stopPropagation(); onDelete(); }}
          style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 15, padding: '0 0 0 4px', lineHeight: 1 }}
        >
          ×
        </button>
      )}
    </div>
  );
}

// ── Confirmation ──────────────────────────────────────────────────────────────────────
//
// `window.confirm` blocks the whole tab, cannot be styled, and — the reason this exists —
// cannot be driven by a test or reached by anything other than a real mouse. This is the
// plain version: a question and two buttons.
//
// It is deliberately NOT ImpactDialog. That one states a cost it fetched from the server
// (campaigns about to lose values) and is coupled to the taxonomy version API. Where there
// is a cost to state, use ImpactDialog. Where the question really is just "are you sure",
// use this and put the consequence in `body`.

export function ConfirmDialog({
  title: t, body, confirmLabel = 'Confirm', tone = 'default', onCancel, onConfirm,
}) {
  const confirmRef = useRef(null);

  // Focus lands on the confirm button so the keyboard path is one Enter, and Escape backs
  // out — the two things the native dialog got right and are easy to lose here.
  useEffect(() => { confirmRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    // Above any modal it may be raised from — a confirmation is always the top-most thing
    // on screen, and one of these opens from inside the version-history modal.
    <div className="o-scrim" style={{ zIndex: 1100 }} onClick={onCancel}>
      <div className="o-modal" style={{ maxWidth: 460 }} role="alertdialog" aria-modal="true"
        onClick={e => e.stopPropagation()}>
        <div className="o-modal-head"><h2>{t}</h2></div>
        {body && (
          <div className="o-modal-body">
            <p style={{ margin: 0, fontSize: 13, color: C.dim }}>{body}</p>
          </div>
        )}
        <div className="o-modal-foot">
          <button className="o-btn" onClick={onCancel}>Cancel</button>
          <button ref={confirmRef}
            className={`o-btn ${tone === 'danger' ? 'o-btn-danger' : 'o-btn-primary'}`}
            onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Promise-based wrapper, so an existing `if (!window.confirm(...)) return;` becomes
 * `if (!await ask({ ... })) return;` without restructuring the handler around callbacks.
 *
 *   const [confirmUI, ask] = useConfirm();
 *   ...
 *   return (<>{confirmUI} ... </>);
 */
export function useConfirm() {
  const [req, setReq] = useState(null);
  const resolveRef = useRef(null);

  const ask = useCallback((opts) => new Promise(resolve => {
    resolveRef.current = resolve;
    setReq(opts);
  }), []);

  const settle = useCallback((answer) => {
    setReq(null);
    resolveRef.current?.(answer);
    resolveRef.current = null;
  }, []);

  const ui = req
    ? <ConfirmDialog {...req} onCancel={() => settle(false)} onConfirm={() => settle(true)} />
    : null;

  return [ui, ask];
}

// Segmented control (the reference's `.toggle`).
export function TabPills({ tabs, active, onChange }) {
  return (
    <div className="o-toggle">
      {tabs.map(t => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          aria-selected={active === t.id}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
